<template>
  <!-- SPEC-ECOUNT-ETL-OBSERVABILITY-001 Phase β — ECAPI 세션 패널 (AC-FE-002, AC-FE-005, AC-FE-006, AC-FE-009) -->
  <a-card class="session-panel" style="margin-bottom: 24px;">
    <template #title>
      <span>{{ $t('ecountEtl_obs_session_title') }}</span>
      <!-- 세션 상태 badge (4값) -->
      <a-tag
        v-if="session"
        :color="sessionBadgeColor"
        style="margin-left: 8px;"
      >
        {{ sessionStateLabel }}
      </a-tag>
    </template>
    <template #extra>
      <!-- 메뉴 노출 계정 전용 액션 (AC-FE-005) -->
      <template v-if="isSuperAdmin">
        <!-- 로그인 토큰 설정 버튼 (구 수동 재로그인) -->
        <a-button
          size="small"
          type="primary"
          ghost
          :loading="reauthLoading"
          @click="onReauth"
        >
          {{ $t('ecountEtl_obs_session_reauth') }}
        </a-button>
        <!-- 세션 강제 만료 버튼 제거: 로그인 토큰 재설정이 세션을 덮어쓰므로 불필요 -->
      </template>
    </template>

    <!-- batch unreachable 표시 -->
    <a-alert
      v-if="error && error.type === 'unreachable'"
      type="error"
      :message="$t('ecountEtl_obs_unreachable')"
      style="margin-bottom: 12px;"
      show-icon
    />

    <!-- 일반 에러 -->
    <a-alert
      v-else-if="error"
      type="error"
      :message="error.message || $t('ecountEtl_obs_error_unknown')"
      style="margin-bottom: 12px;"
      show-icon
    />

    <!-- 로딩 중 -->
    <div v-if="loading" style="text-align: center; padding: 24px;">
      <a-spin />
    </div>

    <!-- 세션 정보 표시 -->
    <a-descriptions
      v-else-if="session"
      :column="{ xs: 1, sm: 2 }"
      size="small"
      bordered
    >
      <a-descriptions-item :label="$t('ecountEtl_obs_session_acquiredAt')">
        {{ formatDateTime(session.acquiredAt) }}
      </a-descriptions-item>
      <a-descriptions-item :label="$t('ecountEtl_obs_session_expiresAt')">
        {{ formatDateTime(session.expiresAt) }}
      </a-descriptions-item>
      <a-descriptions-item :label="$t('ecountEtl_obs_session_remainingMs')">
        {{ formatRemaining(session.remainingMs) }}
      </a-descriptions-item>
      <a-descriptions-item :label="$t('ecountEtl_obs_session_refreshCount')">
        {{ session.refreshCount ?? 0 }}
      </a-descriptions-item>
      <a-descriptions-item :label="$t('ecountEtl_obs_session_lastReauthAt')">
        {{ formatDateTime(session.lastReauthAt) }}
      </a-descriptions-item>
      <a-descriptions-item :label="$t('ecountEtl_obs_session_lastRefreshError')">
        {{ session.lastRefreshError || '-' }}
      </a-descriptions-item>
      <a-descriptions-item :label="$t('ecountEtl_obs_session_tenantKey')" :span="2">
        {{ session.tenantKey }}
      </a-descriptions-item>
    </a-descriptions>

    <!-- 정상 만료 안내: 세션은 동기화 시에만 유지되므로, 에러 없는 EXPIRED는 비정상이 아님 -->
    <a-alert
      v-if="session && isHealthyExpiry"
      type="info"
      :message="$t('ecountEtl_obs_session_expired_normal_hint')"
      style="margin-top: 12px;"
      show-icon
    />

    <!-- 세션 없음 (NONE 상태) -->
    <a-empty
      v-else-if="!loading && !error"
      :description="$t('ecountEtl_obs_session_none')"
    />

    <!-- 액션 결과 toast 영역 -->
    <a-alert
      v-if="actionResult"
      :type="actionResult.type"
      :message="actionResult.message"
      style="margin-top: 12px;"
      show-icon
      closable
      @close="actionResult = null"
    />
  </a-card>
</template>

<script setup>
// @MX:SPEC: SPEC-ECOUNT-ETL-OBSERVABILITY-001 AC-FE-002, AC-FE-005, AC-FE-006, AC-FE-009

import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { postSessionReauth } from '../../../apis/ecountEtlObservability.js';

// Props — 부모(Dashboard.vue)에서 세션 데이터/로딩/에러 주입
const props = defineProps({
  // SessionStatsDto | null
  session: {
    type: Object,
    default: null,
  },
  // 로딩 상태
  loading: {
    type: Boolean,
    default: false,
  },
  // 에러 객체 { type: 'unreachable'|'error', message?, reason? } | null
  error: {
    type: Object,
    default: null,
  },
  // SUPER_ADMIN 여부 (JWT 역할 기반, AC-FE-005)
  isSuperAdmin: {
    type: Boolean,
    default: false,
  },
});

// Emit — 세션 갱신 요청
const emit = defineEmits(['session-updated', 'refresh']);

const { t } = useI18n();

// 로그인 토큰 설정(재로그인) 로딩
const reauthLoading = ref(false);
// 액션 결과 알림
const actionResult = ref(null);

// 세션 상태별 badge 색상 (ACTIVE=green / EXPIRING_SOON=orange / EXPIRED=red / NONE=default)
const sessionBadgeColor = computed(() => {
  if (!props.session) return 'default';
  switch (props.session.state) {
    case 'ACTIVE':        return 'green';
    case 'EXPIRING_SOON': return 'orange';
    case 'EXPIRED':       return 'red';
    case 'NONE':
    default:              return 'default';
  }
});

// 정상 만료 여부 — EXPIRED 이면서 마지막 갱신 에러가 없으면 비정상 아님(동기화 창 밖 자연 만료)
const isHealthyExpiry = computed(() => {
  return props.session?.state === 'EXPIRED' && !props.session?.lastRefreshError;
});

// 세션 상태 라벨 (i18n)
const sessionStateLabel = computed(() => {
  if (!props.session) return '';
  const stateKey = `ecountEtl_obs_session_state_${props.session.state?.toLowerCase() ?? 'none'}`;
  return t(stateKey);
});

/**
 * datetime 포맷 — ISO → 'YYYY-MM-DD HH:mm:ss' (KST +09:00)
 * @param {string|null} iso
 * @returns {string}
 */
function formatDateTime(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      timeZone: 'Asia/Seoul',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * remainingMs → locale에 맞는 '{min}분 {sec}초' 형식
 * SPEC-CHINA-ADMIN-LOCALE-PHASE1-001 영역 E REQ-LOCALE-P1-FE-050~052
 * @param {number|null} ms
 * @returns {string}
 */
function formatRemaining(ms) {
  if (ms == null || ms <= 0) return '-';
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  // REQ-LOCALE-P1-FE-050~052: 한국어 단위 → i18n 키 사용
  const minLabel = t('ecountEtl_session_duration_min', { value: min });
  const secLabel = t('ecountEtl_session_duration_sec', { value: sec });
  return min > 0 ? `${minLabel} ${secLabel}` : secLabel;
}

/**
 * [수동 재로그인] 버튼 핸들러 (AC-FE-006)
 * confirm dialog 후 POST /ecount-etl/session/reauth 호출
 */
async function onReauth() {
  const confirmed = window.confirm(t('ecountEtl_obs_session_reauth_confirm'));
  if (!confirmed) return;

  reauthLoading.value = true;
  actionResult.value = null;

  try {
    const res = await postSessionReauth();
    const { slackNotified } = res.data;

    // 세션 패널 즉시 갱신 (응답 8 필드 사용)
    emit('session-updated', res.data);

    // Slack 알림 결과에 따라 toast 분기
    if (slackNotified) {
      actionResult.value = {
        type: 'success',
        message: t('ecountEtl_obs_session_reauth_success'),
      };
    } else {
      actionResult.value = {
        type: 'warning',
        message: t('ecountEtl_obs_session_reauth_slack_fail'),
      };
    }
  } catch (e) {
    const status = e?.response?.status;
    if (status === 403) {
      actionResult.value = { type: 'error', message: t('ecountEtl_obs_error_403') };
    } else {
      actionResult.value = {
        type: 'error',
        message: e?.response?.data?.message || t('ecountEtl_obs_error_unknown'),
      };
    }
  } finally {
    reauthLoading.value = false;
  }
}
</script>
